import { useCallback } from "react";
import { Formik } from "formik";
import { Transaction } from "@common/types/transaction";
import {
    MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE,
    isTransactionFamilyMatchedToInvoiceOrBill,
} from "@common/helpers/transactions";
import { useEntities } from "@hooks/useEntities";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toDisplayDateOnly } from "@helpers/date";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { fromMonetaryToInt } from "@common/helpers/monetary";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { StandardModalFooter } from "@design-system/Modal/Modal";
import { TransactionSplitSpreadsheet } from "./TransactionSplitSpreadsheet";
import {
    SplitLedgersByEntityId,
    TransactionSplitFormRow,
    getSplitFormLedgerAccounts,
    hasRowMissingCashAccount,
} from "./transactionSplitUtils";
import styles from "./TransactionSplitModal.module.scss";

interface Props {
    transaction: Transaction;
    ledgersByEntityId: SplitLedgersByEntityId;
    onSubmit: (values: { splits: TransactionSplitFormRow[] }) => Promise<void>;
    onUnsplit: () => void;
    loading: boolean;
}

export const TransactionSplitContainer = ({
    transaction,
    ledgersByEntityId,
    onSubmit,
    onUnsplit,
    loading,
}: Props) => {
    const entities = useEntities({ excludeMockEntities: true });
    const isLocked = useIsDateLocked(
        transaction.entity.id,
        toDisplayDateOnly(transaction.dateOnly ?? transaction.date),
    );
    const { enabled: isGlFirst } = useIsGlFirst();
    const isMatchLocked =
        isTransactionFamilyMatchedToInvoiceOrBill(transaction);

    const validateSplits = useCallback(
        (values: { splits: TransactionSplitFormRow[] }) => {
            const errors: { splits?: string } = {};
            const total = values.splits.reduce(
                (sum, s) => sum + fromMonetaryToInt(s.amount ?? 0),
                0,
            );
            const originalTotal = fromMonetaryToInt(transaction.originalAmount);
            if (total !== originalTotal) {
                errors.splits =
                    "Split amounts do not add up to the original transaction amount.";
            } else if (values.splits.some((s) => !s.splitDescription?.trim())) {
                errors.splits =
                    "All splits must have a description before saving.";
            } else if (!isGlFirst && values.splits.some((s) => !s.categoryId)) {
                errors.splits =
                    "All splits must have a category before saving.";
            } else if (
                isGlFirst &&
                hasRowMissingCashAccount({ splits: values.splits, entities })
            ) {
                errors.splits =
                    "All splits must have a cash ledger account before saving.";
            }
            return errors;
        },
        [transaction.originalAmount, isGlFirst, entities],
    );

    const initialSplits: TransactionSplitFormRow[] =
        transaction.splits!.length > 0
            ? transaction
                  .splits!.map(
                      (split) =>
                          ({
                              id: split.id,
                              amount: split.amount,
                              entityId: split.entity.id,
                              splitDescription: split.splitDescription ?? "",
                              categoryId: split.categoryId,
                              classIds:
                                  split.classAssignments?.map(
                                      (ca) => ca.classId,
                                  ) ?? [],
                              counterpartyId: split.counterpartyId ?? undefined,
                              ...getSplitFormLedgerAccounts({
                                  transaction: split,
                                  ledgersByEntityId,
                              }),
                          }) satisfies TransactionSplitFormRow,
                  )
                  .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
            : [
                  {
                      id: transaction.id,
                      amount: transaction.originalAmount,
                      entityId: transaction.entity.id,
                      splitDescription: transaction.displayDescription ?? "",
                      categoryId: transaction.categoryId,
                      classIds:
                          transaction.classAssignments?.map(
                              (ca) => ca.classId,
                          ) ?? [],
                      counterpartyId: transaction.counterpartyId ?? undefined,
                      ...getSplitFormLedgerAccounts({
                          transaction,
                          ledgersByEntityId,
                      }),
                  },
              ];
    return (
        <Formik
            initialValues={{ splits: initialSplits }}
            validate={validateSplits}
            onSubmit={onSubmit}
        >
            {({ handleSubmit, errors, submitCount }) => {
                const errorMessage =
                    submitCount > 0 && typeof errors.splits === "string"
                        ? errors.splits
                        : null;
                return (
                    <>
                        <TransactionSplitSpreadsheet
                            transaction={transaction}
                            entities={entities}
                        />
                        <StandardModalFooter className={styles.footer}>
                            <ButtonWithLoader
                                disabled={loading || isLocked || isMatchLocked}
                                loading={false}
                                tooltip={
                                    isMatchLocked
                                        ? MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE
                                        : undefined
                                }
                                variant="secondary"
                                size="md"
                                onClick={onUnsplit}
                            >
                                Unsplit
                            </ButtonWithLoader>
                            <div className={styles.footerRight}>
                                {isMatchLocked && (
                                    <p className={styles.footerHint}>
                                        {
                                            MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE
                                        }
                                    </p>
                                )}
                                {errorMessage && (
                                    <p className={styles.footerError}>
                                        {errorMessage}
                                    </p>
                                )}
                                <ButtonWithLoader
                                    disabled={isLocked || Boolean(errorMessage)}
                                    loading={loading}
                                    onClick={() => handleSubmit()}
                                    variant="primary"
                                    size="md"
                                >
                                    Save Split
                                </ButtonWithLoader>
                            </div>
                        </StandardModalFooter>
                    </>
                );
            }}
        </Formik>
    );
};
